import React, { useCallback, useEffect, useMemo } from 'react';
import classNames                                 from 'classnames';
import DV                                         from 'ts-data-validator';
import { LocalStorageService }                    from 'ts-storage-service';
import { useTranslation }                         from 'react-i18next';

import { useModal }        from '../../providers/modal';
import { useUser }         from '../../providers/user';
import renderStaticSection from '../../templates/_common/utils/renderStaticSection';

import CloseIcon from '../icons/CloseIcon';

import Notification from './components/form/Notification';

const Modal = function() {
  const {
          body,
          className,
          hideModal,
          large,
          left,
          open,
          title,
          top,
        }                            = useModal();
  const { appealInitialStateLoaded } = useUser();

  const [show, setShow] = React.useState(false);
  const { t }           = useTranslation();
  const modalRef        = React.useRef(null);
  const ls              = new LocalStorageService('user');

  const closeModal = useCallback(() => {
    const currentDate  = new Date();
    const oneYearLater = new Date(currentDate.setFullYear(currentDate.getFullYear() + 1));

    ls.setData('visited', true, oneYearLater);
    show && setShow(false);

    setTimeout(() => {
      hideModal();

      if (DV.isObject(body) && body.hasOwnProperty('props') && body.props.hasOwnProperty('onClose')) {
        DV.isFunction(body.props.onClose) && body.props.onClose();
      }
    }, 250);
  }, [body, hideModal, show]);

  const keyDown = useCallback(({ keyCode }) => {
    if (open && show && keyCode === 27) {
      closeModal();
    }
  }, [closeModal, open, show]);

  const handleClickOutside = useCallback((event) => {
    if (open && (modalRef.current && !modalRef.current.contains(event.target))) {
      closeModal();
    }
  }, [closeModal, open]);

  const confirm = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    show && setShow(false);

    if (DV.isObject(body) && body.hasOwnProperty('props') && body.props.hasOwnProperty('onSave')) {
      DV.isFunction(body.props.onSave) && body.props.onSave();
    }

    setTimeout(hideModal, 250);
  }, [body, hideModal, show]);

  const handleMouseDown = useCallback((event) => {
    const body = modalRef.current.querySelector('.body > .table > div');
    if (!body) {
      return;
    }

    const startX     = event.clientX;
    const scrollLeft = body.scrollLeft;

    const handleMouseMove = (event) => {
      const deltaX    = event.clientX - startX;
      body.scrollLeft = scrollLeft - deltaX;
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

  }, []);

  const sections = useMemo(() => {
    if (body?.props?.notification) {
      const { description } = body.props.notification;

      try {
        const parsedDescription = JSON.parse(description);
        if (parsedDescription && parsedDescription.SECTIONS) {
          return parsedDescription.SECTIONS;
        }
      } catch (err) {
        return [];
      }
    }
    return [];
  }, [body]);

  useEffect(() => {
    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', keyDown);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', keyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', keyDown);
    };
  }, [show]);

  useEffect(() => {
    if (open) {
      if (!show) {
        setTimeout(() => {
          setShow(true);
        }, 100);
      }
    }
  }, [open]);

  useEffect(() => {
    document.body.classList.toggle('fixed', open && show);
  }, [open, show]);

  if (
    !open ||
    (
      DV.isObject(body) &&
      (
        (!body.hasOwnProperty('component') || !body.hasOwnProperty('props')) &&
        !body.hasOwnProperty('SECTIONS') &&
        !body.props?.notification
      )
    )
  ) {
    return null;
  }

  return (
    <>
      {appealInitialStateLoaded && <div className={classNames('modal-overlay', className, { show })}>
        <div
          ref={modalRef}
          className={classNames('modal', className, { large }, {
            'no-padding': !DV.isString(title) || !DV.isNotEmptyString(title),
          })}
          onMouseDown={handleMouseDown}
          style={{
            top:  top && `${top}px`,
            left: left && `calc(${left}px + 20px)`,
          }}
        >
          <span
            className={'close'}
            onClick={closeModal}
          >
            <CloseIcon />
          </span>
          {DV.isString(title) && title.length > 0 && (
            <section className={'head'}>
              <div className={'title'}>{t(title)}</div>
            </section>
          )}
          {DV.isString(body) && (
            <section className={'body'} dangerouslySetInnerHTML={{ __html: body }} />
          )}
          {sections.length > 0 ? (
            <section className={'body'}>
              {sections.map((section, s) => renderStaticSection(section, s))}
              <Notification
                isFormVisible={body?.props?.notification?.isFormVisible}
                closeModal={closeModal}
              />
            </section>
          ) : (
            DV.isObject(body) && (body.hasOwnProperty('SECTIONS') ? (
                <section className={'body'}>
                  {body.SECTIONS.map((section, s) => renderStaticSection(section, s))}
                </section>
              ) : (
                <section className={'body'}>
                  <body.component {...body.props} />
                </section>
              )
            ))}
          <section className={'footer'}>
            {DV.isFunction(((body || {}).props || {}).onSave) && (
              <span
                className={'confirm'}
                onClick={confirm}
              >
                {t('client_all_right')}
              </span>
            )}
          </section>
        </div>
      </div>}
    </>
  );
};

export default Modal;